import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { MENU_CATEGORIES, type MenuCategory } from '../data/menu';
import type { ViewId } from '../types/navigation';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
type Props = {
  activeCategory: string;
  onSelectCategory: (id: string) => void;
  onNavigate: (view: ViewId) => void;
};
export function Menu({
  activeCategory,
  onSelectCategory,
  onNavigate
}: Props) {
  const category = MENU_CATEGORIES.find(item => item.id === activeCategory) ?? MENU_CATEGORIES[0];
  return <>
      {/* Header + category switcher */}
      <section className="px-6 pt-16 md:pt-24 pb-10" data-id="element-399" data-mp="2265lmbr525kq">
        <div className="max-w-6xl mx-auto" data-id="element-400" data-mp="38kwogkzto0he">
          <p className="font-sans text-[10px] md:text-xs tracking-[0.3em] uppercase text-ink/45" data-id="element-401" data-mp="38k4u1dhyo8sp">
            Lilla V
          </p>
          <h1 className="mt-5 font-serif text-5xl sm:text-6xl md:text-7xl font-light leading-[0.95] text-ink" data-id="element-402" data-mp="3dlkdx28ek99i">
            Menyn
          </h1>

          <nav aria-label="Menykategorier" className="mt-10 flex flex-wrap gap-2" data-id="element-403" data-mp="1o8cup90s3p8a">
            {MENU_CATEGORIES.map(item => {
            const isActive = item.id === category.id;
            return <button key={item.id} type="button" onClick={() => onSelectCategory(item.id)} aria-current={isActive ? 'true' : undefined} className={`px-5 py-3 rounded-full border font-sans text-[10px] md:text-[11px] tracking-[0.18em] uppercase transition-colors duration-200 ${isActive ? 'border-ink bg-ink text-white' : 'border-ink/20 text-ink/60 hover:border-ink/50 hover:text-ink'}`} data-id="element-404" data-mp="1ebhvenwolksy">
                  {item.title}
                </button>;
          })}
            <button type="button" onClick={() => onNavigate('vin')} className="px-5 py-3 rounded-full border border-wine/40 text-wine font-sans text-[10px] md:text-[11px] tracking-[0.18em] uppercase hover:border-wine hover:bg-wine hover:text-white transition-colors duration-200" data-id="element-405" data-mp="bw2q6ul4dkkq">
              Lite Vin
            </button>
          </nav>
        </div>
      </section>

      <CategoryPanel key={category.id} category={category} data-id="element-406" data-mp="vmxxpqfspad0" />

      {/* Footer CTA */}
      <section className="px-6 py-20 md:py-24 border-t border-ink/10" data-id="element-407" data-mp="zln8db9g8dsy">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-id="element-408" data-mp="ltyylemoa1f5">
          <p className="font-serif italic text-xl md:text-2xl font-light text-ink/70" data-id="element-409" data-mp="qsjzzhdnur56">
            Vill du ha mackor klara vid disken?
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-410" data-mp="1ja5svfjif31b">
            <button type="button" onClick={() => onNavigate('forbestall')} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-ink/25 text-ink font-sans text-[10px] md:text-xs tracking-[0.2em] uppercase hover:border-ink transition-colors duration-200" data-id="element-411" data-mp="1jbyyqsmatj89">
              Förbeställ
              <ArrowUpRightIcon strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-412" data-mp="1xfo76nj30s61" />
            </button>
            <button type="button" onClick={() => onNavigate('boka')} className="inline-flex items-center px-7 py-3.5 rounded-full bg-ink text-white font-sans text-[10px] md:text-xs tracking-[0.2em] uppercase hover:bg-ink/85 transition-colors duration-200" data-id="element-413" data-mp="19ptvwaeevbnw">
              Boka bord
            </button>
          </div>
        </div>
      </section>
    </>;
}
function CategoryPanel({
  category
}: {
  category: MenuCategory;
}) {
  return <motion.section initial="hidden" animate="visible" variants={{
    visible: {
      transition: {
        staggerChildren: 0.06
      }
    }
  }} className="px-6 pb-8" aria-labelledby={`kat-${category.id}`} data-id="element-414" data-mp="2q4vdi8os2m1u">
      <div className="max-w-6xl mx-auto" data-id="element-415" data-mp="14lt5eev7c8or">
        {/* Category head */}
        <motion.div variants={fadeUp} className="pt-10 md:pt-14 border-t border-ink/15" data-id="element-416" data-mp="19lxkq1kdixde">
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-2" data-id="element-417" data-mp="1dnj4m6vbsbx3">
            <h2 id={`kat-${category.id}`} className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-tight text-ink" data-id="element-418" data-mp="1imdozd8xy8jb">
              {category.title}
            </h2>
            <span className="font-sans text-[11px] tracking-[0.2em] uppercase text-wine" data-id="element-419" data-mp="1sbx4c8xg3k9f">
              {category.tagline}
            </span>
          </div>
          {category.intro && <p className="mt-5 max-w-xl font-sans text-sm leading-relaxed text-ink/55" data-id="element-420" data-mp="21ddgal3wrufr">
              {category.intro}
            </p>}
        </motion.div>

        {/* Items */}
        {category.items.length > 0 && <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-10 md:gap-y-12" data-id="element-421" data-mp="3qus5wwky1443">
            {category.items.map(item => <motion.article key={item.name} variants={fadeUp} data-id="element-422" data-mp="3htaqzhj9v8gz">
                <div className="flex items-baseline gap-3" data-id="element-423" data-mp="3htkkhf3izxrf">
                  <h3 className="font-serif text-2xl md:text-[1.75rem] font-medium leading-tight text-ink whitespace-nowrap" data-id="element-424" data-mp="3cuzm7l7u3o5m">
                    {item.name}
                  </h3>
                  <span aria-hidden="true" className="flex-1 border-b border-dotted border-ink/25 translate-y-[-0.25rem]" data-id="element-425" data-mp="9dr18sht886o" />
                  <span className="font-sans text-sm tabular-nums text-ink/55" data-id="element-426" data-mp="3vt3hpcustooy">
                    {item.price ?? '—'}
                  </span>
                </div>
                <p className="mt-2.5 max-w-lg font-sans text-[13px] md:text-sm leading-relaxed text-ink/55" data-id="element-427" data-mp="x4muzv5ttdgk">
                  {item.ingredients}
                </p>
              </motion.article>)}
          </div>}

        {/* Images */}
        {category.images.length > 0 && <motion.div variants={fadeUp} className={`mt-16 md:mt-20 grid grid-cols-2 ${category.columns} gap-3 md:gap-4`} data-id="element-428" data-mp="1g6x1pjyaww05">
            {category.images.map(image => <div key={image.src} className="group relative aspect-[3/4] overflow-hidden bg-ink/5 rounded-sm" data-id="element-429" data-mp="16g2b63e0va8w">
                <div className="w-full h-full transition-transform duration-700 ease-out group-hover:scale-[1.03]" data-id="element-430" data-mp="xhgwwkfl2hdx">
                  <img src={image.src} alt={image.alt} loading="lazy" className="w-full h-full object-cover" style={{
              objectPosition: image.focus ?? 'center',
              transform: image.zoom ? `scale(${image.zoom})` : undefined
            }} draggable={false} data-id="element-431" data-mp="jiqzt4srkhpk" />
                </div>
              </div>)}
          </motion.div>}
      </div>
    </motion.section>;
}